"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import { Star, Shield, BadgeCheck } from "lucide-react";

interface Testimonial {
  name: string;
  role: string;
  userId: string;
  content: string;
  rating: number;
}

interface BasicTestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  subtitle?: string;
}

const TestimonialCard = ({
  name,
  role,
  userId,
  content,
  rating,
}: Testimonial) => {
  return (
    <div className="flex flex-col gap-6 rounded-3xl border border-zinc-100 bg-white p-8 shadow-sm transition-shadow hover:shadow-lg">
      {/* Rating */}
      <div className="flex gap-1">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} size={16} className="fill-primary text-primary" />
        ))}
      </div>

      {/* Quote */}
      <p className="flex-1 text-base font-medium leading-relaxed text-foreground/80">
        &ldquo;{content}&rdquo;
      </p>

      {/* Author */}
      <div className="flex items-center gap-4 border-t border-zinc-100 pt-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-foreground">
          <Shield size={24} className="text-secondary" />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground">{name}</span>
            <BadgeCheck size={16} className="fill-primary text-white" />
          </div>
          <span className="text-xs text-zinc-400">
            {role} · <span className="font-mono">{userId}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const BasicTestimonials = ({
  testimonials,
  title = "What people are saying",
  subtitle = "Trusted by millions",
}: BasicTestimonialsProps) => {
  return (
    <section className="py-24 bg-muted">
      <div className="container max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 mb-16">
          <BlurFade delay={0.1}>
            <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
              {subtitle}
            </p>
          </BlurFade>
          <BlurFade delay={0.2}>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
              {title}
            </h2>
          </BlurFade>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <BlurFade key={testimonial.name} delay={0.3 + index * 0.1}>
              <TestimonialCard {...testimonial} />
            </BlurFade>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BasicTestimonials;
